import { AxiosResponse } from 'axios'
import api from 'api'
import {
  ICollection,
  ICourse,
  ILearningPath,
  ILearningPathConfiguration,
} from 'interfaces/learningPath'
import { IProfile } from 'interfaces/profiles'
import { CourseLmsProfileType } from 'enum/course-lms-profile-type.enum'

const PROGRAM_SORT_FIELDS: Record<string, string> = {
  name: 'name',
  createdAt: 'created_at',
  state: 'state',
  participants: 'users_count',
  totalCourses: 'courses_count',
}

const mapLearningPaths = (data: { attributes: ILearningPath }[]) =>
  data.map((item) => item.attributes)

const getLearningPaths = async () => {
  const response = await api.post('/administration/learning_paths/search')
  return mapLearningPaths(response.data.data)
}

const searchLearningPaths = async ({
  page,
  perPage,
  search,
  sortField,
  sortOrder,
}: {
  page: number
  perPage: number
  search: string
  sortField: string
  sortOrder: string
}) => {
  const field = PROGRAM_SORT_FIELDS[sortField] || 'name'
  const direction = sortOrder === 'descend' ? 'desc' : 'asc'
  const q: { nameICont?: string; s: string } = {
    s: `${field} ${direction}`,
  }
  const term = search.trim()
  if (term) q.nameICont = term

  const response = await api.post('/administration/learning_paths/search', {
    page,
    perPage,
    q,
  })

  return {
    data: mapLearningPaths(response.data.data),
    total: response.data.meta?.total ?? 0,
  }
}

const removeLearningPath = async (id: number) => {
  const response = await api.delete(`/administration/learning_paths/${id}`)
  return response.data
}

const getConfiguration = async (id: string) => {
  const response = await api.get(`/administration/learning_paths/${id}`)
  return {
    id: response.data.data.attributes.id,
    name: response.data.data.attributes.name,
    description: response.data.data.attributes.description,
    image: response.data.data.attributes.pictureUrl,
    profiles: response.data.data.attributes.lmsProfiles.map(
      (profile: IProfile) => profile.id
    ),
    state: response.data.data.attributes.state,
  }
}

const getContent = async (id: string) => {
  const { data } = await api.get(`/administration/learning_paths/${id}/detail`)
  const collections = data.collections || []
  const learningPaths = data.learningPaths || []
  return {
    collections: collections.map((collection: ICollection) => {
      return {
        id: collection.id,
        name: collection.name,
        order: collection.order,
        relatedCourses: collection.relatedCourses.map((relatedCourse) => {
          return {
            course: relatedCourse.course,
            order: relatedCourse.order,
            prerequisites: relatedCourse.prerequisites,
            required: relatedCourse.required
              ? CourseLmsProfileType.Required
              : CourseLmsProfileType.Optional,
          }
        }),
      }
    }),
    learningPaths,
  }
}

const getProfiles = async () => {
  const response = await api.get('/administration/lms_profiles/list')
  return response.data
}

const saveConfiguration = async (data: ILearningPathConfiguration) => {
  const formData = new FormData()
  formData.append('learning_path[name]', data.name)
  formData.append('learning_path[description]', data.description)
  if (data.image && typeof data.image !== 'string') {
    formData.append('learning_path[picture]', data.image)
  }
  data.profiles.forEach((profile) => {
    formData.append('learning_path[lms_profile_ids][]', String(profile))
  })
  formData.append('learning_path[state]', String(data.state))

  let response: AxiosResponse

  if (data.id) {
    response = await api.put(
      `/administration/learning_paths/${data.id}`,
      formData
    )
  } else {
    response = await api.post('/administration/learning_paths', formData)
  }

  return response.data.data.attributes
}

const saveContent = async (
  id: number,
  state: number,
  learningPaths: number[],
  collections: ICollection[]
) => {
  const data = {
    learning_path: {
      state: state,
      current_data: {
        learning_paths: learningPaths.filter(
          (learningPath) => learningPath !== 0
        ),
        collections: collections.map((collection) => ({
          id: collection.id,
          name: collection.name,
          order: collection.order,
          related_courses: collection.relatedCourses,
        })),
      },
    },
  }
  const response = await api.patch(
    `/administration/learning_paths/${id}/update_program`,
    data
  )
  return response.data.data.attributes
}

const getCourses = async (
  learningPathId: number,
  search: string,
  page: number
) => {
  const response = await api.get(
    `/administration/learning_paths/${learningPathId}/available_courses?page=${page}&name=${search}`
  )
  return {
    courses: response.data.data.map(
      (item: { attributes: ICourse }) => item.attributes
    ),
    total: response.data.meta.total,
  }
}

const getCoursesByLearningPath = async (id: number) => {
  const response = await api.get(
    `/administration/learning_paths/${id}/prerequisites`
  )
  return response.data
}

export const learningPathsService = {
  getLearningPaths,
  searchLearningPaths,
  removeLearningPath,
  getConfiguration,
  getContent,
  getProfiles,
  saveConfiguration,
  saveContent,
  getCourses,
  getCoursesByLearningPath,
}
